<template>
  <div class="app-cpost flex flex-col w-full sm:max-w-500 m-auto">
    <div v-if="!addressFallback">
      <!-- Street Address Field -->

      <AppInput
        id="street_address"
        ref="control"
        name="street_address"
        class="app-cpost__input"
        :label="cPostInputText"
        autocomplete="off"
        max-width="lg:max-w-550"
        :icon-right="!!addressValue.verified"
        :value="streetAddress"
        :theme="theme"
        :error="cPostError"
        @input="updateStreetAddress($event)"
      >
        <FontAwesomeIcon
          v-if="addressValue.verified"
          slot="iconRight"
          icon="circle-check"
          :class="textColorClass()"
        />

        <template
          v-if="cPostError"
          #error
        >
          <small
            class="text-gray-500"
          >
            We could not find your address, please correct it or
            <u
              class="cursor-pointer"
              @click="showFallback"
            >
              type it in manually
            </u>.
          </small>
        </template>
      </AppInput>

      <!-- Results -->
      <AppCanadaPostResult
        :address="addressValue"
        :theme="theme"
      />
    </div>

    <AppCanadaPostFallback
      v-if="addressFallback"
      :theme="theme"
      @fallback-validated="$emit('fallback-validated')"
    />
  </div>
</template>

<script>
import AppCanadaPostFallback from '@/components/common/canada-post/AppCanadaPostFallback'
import AppCanadaPostResult from '@/components/common/canada-post/AppCanadaPostResult'
import AppInput from '@/components/common/inputs/AppInput'
import cpostFields from './cpostFields'
import Axios from '@/utilities/axios-instance'
import input from '@/mixins/input'
import theme from '@/mixins/theme'
import DataLayer from '@/utilities/data-layer'

/* eslint-disable no-unused-expressions */
import(/* webpackChunkName: "addresscomplete" */ '@/assets/js/addresscomplete-2.30.min.js')
/* eslint-enable no-unused-expressions */

export default {
  // Name
  name: 'AppCanadaPost',

  // Components
  components: {
    AppCanadaPostFallback,
    AppCanadaPostResult,
    AppInput
  },

  // Mixins
  mixins: [
    input,
    theme
  ],

  // Props
  props: {
    theme: {
      type: String,
      default: 'green'
    }
  },

  // Data
  data () {
    return {
      control: undefined,
      fallbackListener: undefined,
      streetAddress: undefined,
      loading: false,
      cpostOffline: false,
      cpostKey: process.env.VUE_APP_CPOST_KEY,
      formFields: [{ element: 'street_address', field: 'Line1' }]
    }
  },

  // Computed
  computed: {

    cPostError () {
      return this.$store.state.validations.cPostError
    },

    locationState () {
      return this.$store.state.location
    },

    addressValue () {
      return {
        verified: this.locationState.c_post_verified,
        line1: this.locationState.c_post_Line1,
        line2: this.locationState.c_post_Line2,
        city: this.locationState.c_post_City,
        province: this.locationState.c_post_Province,
        postalCode: this.locationState.c_post_PostalCode,
        fullAddress: this.locationState.c_post_fullAddress
      }
    },

    addressFallback () {
      return this.locationState.addressFallback
    },

    fallBackDataCompleted () {
      return this.$store.getters.fallbackDataCompleted
    },

    isPersonalLoan () {
      return this.$store.getters['loans/isPersonalLoan']
    },

    /**
     * Toggles floating label text based on address verified status
    */
    toggleLabelText () {
      return !this.addressValue.verified
        ? 'Begin Typing Address Here'
        : 'Street Address'
    },

    cPostInputText () {
      if (this.$route.meta.cPostInputText) return this.$route.meta.cPostInputText
      else return 'My Home Address'
    }
  },

  created () {
    if (!this.fallBackDataCompleted) this.$store.dispatch('setAddressFallback', false)
  },

  // Mounted
  mounted () {
    this.canadaPostInit() // Initialize cpost

    if (this.addressValue.fullAddress) {
      this.streetAddress = this.addressValue.line1
    }

    // Unhide the pca if this step isn't using the address fallback
    const pcaElement = document.querySelector('.pca')
    if (!this.addressFallback && pcaElement) {
      document.querySelector('.pca').classList.remove('hide')
      document.querySelector('.pca').style.display = 'block'
    }
  },

  // Before Destroy
  beforeDestroy () {
    if (window.control) window.control.destroy()
    document.querySelector('.pcatext.pcanotification').remove()
  },

  // Methods
  methods: {
    /**
     * Canada Post Init
     * @desc Initialize the canada post auto complete input 'control'
     */
    async canadaPostInit () {
      try {
        if (!window.control) {
          await this.sendTestRequest()
        }
        addressComplete.destroy()
      } catch (error) {
        console.warn('error with cpost api')
      }
      // Canada Post API settings
      const options = {
        key: this.cpostKey,
        countries: {
          value: 'CAN'
        }
      }

      // Autocomplete Input Control
      if (window.pca) window.control = new pca.Address(this.formFields, options)

      // add window.control handlers to PCA instance
      this.initControlHandlers()
    },

    /**
     * Send Test Request
     * @desc tests the Cpost to see if it's working
     * If it returns an error, show the fallback
     */
    async sendTestRequest () {
      try {
        this.loading = true
        const response = await Axios.get(
          '//ws1.postescanada-canadapost.ca/AddressComplete/Interactive/Find/v2.10/json3ex.ws?Key=' + this.cpostKey + '&Country=CAN&SearchTerm=a&LanguagePreference=en',
          { timeout: process.env.VUE_APP_CPOST_TIMEOUT || 5000 }
        )
        if (response.data.Items[0].Error) {
          throw new Error()
        }
      } catch (error) {
        this.cpostOffline = true
        this.$store.dispatch('setCPostError', 1)
        this.showFallback(error)
      } finally {
        this.loading = false
      }
    },

    /**
     * Populate the address fields
     * @emits populate
     */
    populateAddress (address) {
      address.verified = 1
      address.error = 0

      cpostFields.forEach(item => {
        this.$store.dispatch(item.vuexAction, address[item.key])
      })

      this.streetAddress = `${address.FormattedLine1}, ${address.City} ${address.Province}`

      this.$emit('populate')
      let event = 'address_selected'
      if (this.isPersonalLoan) event = 'spring_ploan_address_selected'
      DataLayer.pushEvent('event', event)
    },

    /**
     * Update Street Address
     * Resets 'address.verified' to false
     * @emits input
     */
    updateStreetAddress (value) {
      this.$emit('input')
      this.streetAddress = value
      this.$store.dispatch('setCpostVerified', 0)

      this.$nextTick(() => {
        if (!window.control) return
        window.control.reposition()
        this.showFallbackPlaceholder()
      })
    },

    /**
     * Attach the CPost listeners for populate success / error
     */
    initControlHandlers () {
      // check before calling window.control/window.control.listen
      // to prevent console errors when Canada Post is offline.
      if (window.control && window.control.listen) {
        // 'populate' event fires when the user selects an address
        window.control.listen('populate', (address) => {
          this.populateAddress(address)
        })

        // 'error' event fires if addressComplete encouters an error
        window.control.listen('error', (error) => {
          console.warn(error)
          this.$store.dispatch('setCPostError', 1)
          this.showFallback(error)
        })
      }
    },

    /**
     * Show Fallback Placeholder Text
     */
    showFallbackPlaceholder () {
      // Grab the list of canada post footer elements (there are 2)
      const pcafooters = document.querySelectorAll('.pcafooter')
      const pcafooter = pcafooters[0]

      // If address length > 1, Add fallback text class .show-fallback-text
      if (!this.streetAddress) {
        return
      }
      if (this.streetAddress.length > 1) {
        this.$nextTick(() => {
          if (pcafooter) {
            pcafooter.classList.remove('no-click')
            pcafooter.classList.add('show-fallback-text')
            // Add a click listener to the .pcafooter
            this.fallbackListener = pcafooter.addEventListener('click', () => {
              // Show the fallback fields
              this.showFallback('manual address fallback')
              document.querySelector('.pca').classList.add('hide')
            }, false)
          }
        })
      } else {
        // Remove the .show-fallback-text class and click listener
        pcafooter.classList.remove('show-fallback-text')
        pcafooter.classList.add('no-click')
        pcafooter.removeEventListener('click', this.fallbackListener)
        this.fallbackListener = undefined
      }
    },

    /**
     * Show Address Fallback
     */
    showFallback (error) {
      this.$store.dispatch('setAddressFallback', true)

      // Hide PCA if we are showing the fallback
      document.querySelector('.pca').style.display = 'none'

      DataLayer.pushEvent('event', 'address_manual')
      if (error?.message) {
        this.$store.dispatch('setCPostErrorDescription', error.message)
      }
    }
  }
}
</script>

<style lang="css">
.pcaflagbutton,
.pcamessage,
.aclogo {
  display: none;
}

.pcafooter.show-fallback-text::before {
  content: 'I don\'t see my address. Let me type it in.';
  display: block;
  font-size: 13px;
  margin-left: 12px;
  margin-bottom: 0;
  font-weight: 600;
  text-decoration: underline;
  padding-top: 7px;
  cursor: pointer;
}

.pcaautocomplete {
  max-width: 90%;
}

.pcafooter.no-click {
  pointer-events: none;
}
</style>

<style lang="css" src="@/assets/css/addresscomplete-2.3.0.min.css"></style>
